import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls } from "@/components/ui-kit";
import { toast } from "sonner";
import { UserPlus, ChevronDown, Search, X, Check } from "lucide-react";

/**
 * Searchable customer picker with inline "quick-create prospect" option.
 *
 * Props:
 *  - value: current customer_id
 *  - onChange: (customerId) => void
 *  - onCustomerCreated?: (customer) => void  (fires after quick-create)
 *  - includeInactive?: bool
 *  - required?: bool
 *  - placeholder?: string
 *  - testid?: string  (root wrapper testid)
 *  - showAddress?: bool  (append address to option label)
 */
export default function CustomerSelect({
  value,
  onChange,
  onCustomerCreated,
  includeInactive = false,
  required = false,
  placeholder = "— Selecciona un cliente —",
  testid = "customer-select",
  showAddress = false,
}) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const params = includeInactive ? {} : { active: true };
    const { data } = await api.get("/customers", { params });
    setCustomers(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = customers.find((c) => c.id === value);
  const filtered = query.trim()
    ? customers.filter((c) => {
        const q = query.toLowerCase();
        return (c.name || "").toLowerCase().includes(q)
          || (c.rut || "").toLowerCase().includes(q)
          || (c.address || "").toLowerCase().includes(q)
          || (c.contact_name || "").toLowerCase().includes(q);
      })
    : customers;

  const pick = (c) => {
    onChange(c.id);
    setOpen(false);
    setQuery("");
  };

  const quickCreate = async () => {
    if (!newName.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const { data: created } = await api.post("/customers", {
        name: newName.trim(),
        address: "",
        active: true,
        notes: "Prospecto — datos pendientes de completar",
      });
      toast.success(`Prospecto "${created.name}" creado. Completa los datos desde el detalle del cliente.`);
      setCustomers((prev) => [created, ...prev]);
      onChange(created.id);
      if (onCustomerCreated) onCustomerCreated(created);
      setShowCreate(false);
      setNewName("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-trigger`}
        className={`${inputCls} flex items-center justify-between text-left ${!selected ? "text-neutral-400" : ""}`}
      >
        <span className="truncate">
          {selected ? (
            <>
              <span className="text-neutral-900">{selected.name}</span>
              {showAddress && selected.address && <span className="text-neutral-500"> — {selected.address}</span>}
              {!selected.address && <span className="ml-2 font-mono-kpi text-[9px] uppercase tracking-wider bg-[#FFC107]/15 text-[#B28C00] border border-[#FFC107]/40 px-1.5 py-0.5">Prospecto</span>}
            </>
          ) : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 flex-shrink-0 text-neutral-400" />
      </button>
      {/* Hidden input to make required validation work on submit */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          value={value || ""}
          onChange={() => {}}
          required
          className="absolute opacity-0 pointer-events-none left-2 top-4 w-1 h-1"
        />
      )}

      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white border border-neutral-300 shadow-lg max-h-80 overflow-hidden flex flex-col" data-testid={`${testid}-panel`}>
          <div className="relative border-b border-neutral-200 p-2">
            <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              placeholder="Buscar por nombre, RUT o dirección…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testid}-search`}
              className={`${inputCls} pl-8 py-1.5`}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid={`${testid}-quick-create`}
            className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#FF5A00] hover:bg-[#FF5A00]/5 border-b border-neutral-200"
          >
            <UserPlus className="w-4 h-4" />
            <span>Crear cliente rápido…</span>
            <span className="font-mono-kpi text-[9px] uppercase tracking-wider text-neutral-400 ml-auto">Solo nombre</span>
          </button>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-3 text-xs font-mono-kpi text-neutral-400 uppercase tracking-widest">Cargando…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-sm text-neutral-500">Sin coincidencias. Puedes crear uno nuevo arriba.</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c)}
                  data-testid={`${testid}-opt-${c.id}`}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 ${c.id === value ? "bg-[#FF5A00]/5" : ""}`}
                >
                  {c.id === value && <Check className="w-3.5 h-3.5 text-[#FF5A00]" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-1.5">
                      {c.name}
                      {!c.address && <span className="font-mono-kpi text-[9px] uppercase tracking-wider bg-[#FFC107]/15 text-[#B28C00] border border-[#FFC107]/40 px-1 py-0.5">Prospecto</span>}
                      {!c.active && <span className="font-mono-kpi text-[9px] uppercase tracking-wider bg-neutral-100 text-neutral-500 border border-neutral-300 px-1 py-0.5">Baja</span>}
                    </div>
                    {c.address && <div className="text-xs text-neutral-500 truncate">{c.address}{c.commune ? `, ${c.commune}` : ""}</div>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md border-t-4 border-[#FF5A00]"
            data-testid="customer-quick-create-modal"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <div>
                <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Prospecto rápido</div>
                <div className="font-display font-bold text-xl">Nuevo cliente</div>
              </div>
              <button type="button" onClick={() => setShowCreate(false)} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-3">
              <label className="block">
                <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">Nombre *</div>
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); quickCreate(); } }}
                  placeholder="Ej: Comunidad Torres del Sol"
                  className={inputCls}
                  data-testid="customer-quick-create-name"
                />
              </label>
              <div className="text-xs text-neutral-500 leading-relaxed bg-[#FFC107]/5 border border-[#FFC107]/30 p-3">
                Se creará un cliente en modo <b>prospecto</b>. Podrás completar RUT, dirección, contactos
                y precio UF más tarde desde <b>Clientes → Detalle</b>.
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
              <button type="button" onClick={() => setShowCreate(false)} className="bg-white border border-neutral-300 hover:border-[#0A0A0A] text-[#0A0A0A] px-5 py-2.5 font-mono-kpi text-xs uppercase tracking-[0.15em]">Cancelar</button>
              <button
                type="button"
                onClick={quickCreate}
                disabled={saving}
                data-testid="customer-quick-create-submit"
                className="bg-[#FF5A00] hover:bg-[#E65100] text-white px-5 py-2.5 font-mono-kpi text-xs uppercase tracking-[0.15em] disabled:opacity-50"
              >
                {saving ? "Creando…" : "Crear y seleccionar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
